import { observer } from "mobx-react-lite";
import { EasyAvatar } from "fin-web-components";
import { runInAction } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";
import { App, Input, Modal, Progress, Radio } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  CameraOutlined,
  CopyOutlined,
  ManOutlined,
  UserOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { H5SubPageShell } from "../components/common/H5SubPageShell";
import { H5MobileDatePicker } from "../components/common/H5MobileDatePicker";
import { globalModel } from "../global/GlobalModel";
import { userProfilePageCssVars, USER_PROFILE_PAGE_LAYOUT } from "./Config";
import { UserProfileApi } from "./UserProfileApi";
import { useRequireLoginRedirect } from "../utils/useRequireLoginRedirect";
import { avatarDisplayWord } from "../utils/avatarDisplayWord";
import { copyTextToClipboard } from "../utils/copyTextToClipboard";
import { resolveAppAvatarUrl, resolveAppMediaUrl, toPersistableAvatarUrl } from "../utils/resolveAppMediaUrl";
import "./UserProfileStyle.css";

const BIRTHDAY_PANEL_DEFAULT = dayjs("1990-12-20");
const NICKNAME_MAX_LENGTH = USER_PROFILE_PAGE_LAYOUT.layout.nicknameMaxLength;

type SaveProgressState = {
  open: boolean;
  percent: number;
  label: string;
};

const SAVE_PROGRESS_IDLE: SaveProgressState = {
  open: false,
  percent: 0,
  label: "",
};

export const UserProfilePage = observer(() => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const canView = useRequireLoginRedirect();
  const { message: msgApi } = App.useApp();
  const [nickname, setNickname] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [gender, setGender] = useState<"U" | "M" | "F">("U");
  const [birthday, setBirthday] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState<SaveProgressState>(SAVE_PROGRESS_IDLE);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  /** 待保存时上传的本地头像文件 */
  const pendingAvatarFileRef = useRef<File | null>(null);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setAvatarPreviewUrl(null);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!canView) {
      return;
    }
    revokePreview();
    pendingAvatarFileRef.current = null;
    const u = globalModel.currentUser;
    if (!u) {
      return;
    }
    setNickname((u.name ?? "").trim() || "");
    setAvatarUrl(toPersistableAvatarUrl(u.avatarUrl));
    setGender((u.gender as "U" | "M" | "F") ?? "U");
    const b = u.birthday?.trim();
    if (b) {
      const d = dayjs(b);
      setBirthday(d.isValid() ? d : null);
    } else {
      setBirthday(null);
    }
  }, [canView, revokePreview]);

  const runPickFile = useCallback(
    (file: File) => {
      const isImage =
        file.type.startsWith("image/") ||
        /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(file.name);
      if (!isImage) {
        msgApi.warning("请选择图片");
        return;
      }
      revokePreview();
      const localUrl = URL.createObjectURL(file);
      previewUrlRef.current = localUrl;
      pendingAvatarFileRef.current = file;
      setAvatarPreviewUrl(localUrl);
    },
    [msgApi, revokePreview]
  );

  const letter = avatarDisplayWord(nickname) || "·";
  const displayAvatarUrl =
    avatarPreviewUrl ||
    resolveAppAvatarUrl(avatarUrl) ||
    resolveAppMediaUrl(avatarUrl) ||
    undefined;
  const userId = globalModel.currentUser?.id?.trim() ?? "";

  const onCopyUserId = useCallback(() => {
    if (!userId) {
      return;
    }
    void copyTextToClipboard(userId).then((ok) => {
      if (ok) {
        msgApi.success("已复制用户ID");
      } else {
        msgApi.error("复制失败，请重试");
      }
    });
  }, [msgApi, userId]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f) {
        runPickFile(f);
      }
    },
    [runPickFile]
  );

  const onSubmit = useCallback(async () => {
    const n = nickname.trim();
    if (!n) {
      msgApi.warning("请填写昵称");
      return;
    }
    if (n.length > NICKNAME_MAX_LENGTH) {
      msgApi.warning(`昵称最多 ${NICKNAME_MAX_LENGTH} 字`);
      return;
    }
    if (birthday) {
      if (birthday.isAfter(dayjs(), "day")) {
        msgApi.warning("生日不能晚于今天");
        return;
      }
      if (birthday.isBefore(dayjs("1900-01-01"), "day")) {
        msgApi.warning("生日不早于 1900-01-01");
        return;
      }
    }

    const pendingFile = pendingAvatarFileRef.current;
    setSubmitting(true);
    setSaveProgress({
      open: true,
      percent: 8,
      label: pendingFile ? "上传头像…" : "保存资料…",
    });

    let persistAv = toPersistableAvatarUrl(avatarUrl);

    try {
      if (pendingFile) {
        setSaveProgress({ open: true, percent: 18, label: "上传头像…" });
        const uploadedUrl = await UserProfileApi.uploadAvatarFromCompressedFile(pendingFile);
        if (!uploadedUrl) {
          msgApi.error("头像上传失败，请重试");
          return;
        }
        persistAv = toPersistableAvatarUrl(uploadedUrl);
        setSaveProgress({ open: true, percent: 62, label: "保存资料…" });
      } else {
        setSaveProgress({ open: true, percent: 35, label: "保存资料…" });
      }

      if (persistAv.length > 1024) {
        msgApi.warning("头像信息异常，请重选图片");
        return;
      }

      const ok = await UserProfileApi.updateProfile({
        nickname: n,
        avatarUrl: persistAv,
        gender,
        birthday: birthday ? birthday.format("YYYY-MM-DD") : "",
      });
      if (!ok) {
        msgApi.error("保存失败，请重试");
        return;
      }

      setSaveProgress({ open: true, percent: 100, label: "保存成功" });
      setAvatarUrl(persistAv);

      const bdayYmd = birthday ? birthday.format("YYYY-MM-DD") : undefined;
      const resolvedAvatar = persistAv
        ? resolveAppAvatarUrl(persistAv) ?? resolveAppMediaUrl(persistAv)
        : undefined;
      runInAction(() => {
        const cur = globalModel.currentUser;
        if (cur) {
          globalModel.currentUser = {
            ...cur,
            name: n,
            avatarUrl: resolvedAvatar ?? cur.avatarUrl,
            birthday: bdayYmd,
            gender,
          };
        }
      });
      void globalModel.refreshUserFromServer().then(() => {
        runInAction(() => {
          const cur = globalModel.currentUser;
          if (!cur || !resolvedAvatar) {
            return;
          }
          if (!resolveAppAvatarUrl(cur.avatarUrl) && !resolveAppMediaUrl(cur.avatarUrl)) {
            globalModel.currentUser = {
              ...cur,
              avatarUrl: resolvedAvatar,
            };
          }
        });
      });
      pendingAvatarFileRef.current = null;
      revokePreview();
      msgApi.success("已保存");
    } finally {
      setSubmitting(false);
      setSaveProgress(SAVE_PROGRESS_IDLE);
    }
  }, [avatarUrl, birthday, gender, msgApi, nickname, revokePreview]);

  const backTo = spaceId ? `/space/${spaceId}/mine` : undefined;

  const saveButton = (
    <button
      type="button"
      className="user-profile-page__save-top"
      disabled={submitting}
      onClick={() => void onSubmit()}
    >
      {submitting ? "保存中" : "保存"}
    </button>
  );

  return (
    <H5SubPageShell title="个人资料" backTo={backTo} headerRight={saveButton}>
      <Modal
        open={saveProgress.open}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={280}
        className="user-profile-save-progress"
        title={null}
      >
        <Progress
          percent={saveProgress.percent}
          status={saveProgress.percent >= 100 ? "success" : "active"}
          strokeColor="var(--user-profile-accent, #ec407a)"
        />
        <p className="user-profile-save-progress__label">{saveProgress.label}</p>
      </Modal>
      <div className="user-profile-page" style={userProfilePageCssVars()}>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
          className="user-profile-page__file"
          onChange={onFileChange}
          tabIndex={-1}
        />
        <div className="user-profile-page__avatar-block">
          <div className="user-profile-page__avatar-wrap">
            <button
              type="button"
              className="user-profile-page__avatar-btn"
              disabled={submitting}
              onClick={() => fileRef.current?.click()}
            >
              <EasyAvatar
                shape="circle"
                size={USER_PROFILE_PAGE_LAYOUT.layout.avatarSize}
                url={displayAvatarUrl}
                word={letter}
                alt=""
                className="user-profile-page__avatar-large"
              />
              <span className="user-profile-page__camera" aria-hidden>
                <CameraOutlined />
              </span>
            </button>
          </div>
          {userId ? (
            <div className="user-profile-page__user-id">
              <span className="user-profile-page__user-id-value" translate="no">
                {userId}
              </span>
              <button
                type="button"
                className="user-profile-page__copy"
                onClick={onCopyUserId}
                aria-label="复制用户ID"
              >
                <CopyOutlined />
              </button>
            </div>
          ) : null}
        </div>

        <div className="user-profile-page__field">
          <Input
            className="user-profile-page__input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={NICKNAME_MAX_LENGTH}
            placeholder="昵称"
            showCount
          />
        </div>

        <div className="user-profile-page__field user-profile-page__field--row">
          <div className="user-profile-page__gender">
            <Radio.Group
              className="user-profile-page__radio"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              optionType="button"
            >
              <Radio.Button value="U" title="未选">
                <span className="user-profile-page__gender-inner">
                  <UserOutlined className="user-profile-page__gi" />
                  未选
                </span>
              </Radio.Button>
              <Radio.Button value="M" title="男">
                <span className="user-profile-page__gender-inner">
                  <ManOutlined className="user-profile-page__gi" />
                  男
                </span>
              </Radio.Button>
              <Radio.Button value="F" title="女">
                <span className="user-profile-page__gender-inner">
                  <WomanOutlined className="user-profile-page__gi" />
                  女
                </span>
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>

        <div className="user-profile-page__field">
          <H5MobileDatePicker
            value={birthday}
            onChange={setBirthday}
            allowClear
            title="选择生日"
            placeholder="公历生日，选填"
            defaultPickerValue={BIRTHDAY_PANEL_DEFAULT}
            minDate={dayjs("1900-01-01")}
            maxDate={dayjs()}
            triggerClassName="user-profile-page__date-trigger"
          />
        </div>
      </div>
    </H5SubPageShell>
  );
});

export default UserProfilePage;
